import React, { forwardRef, useState, useContext } from "react";
import PropTypes from "prop-types";
import { Button, Wrapper, Menu, MenuItem } from "react-aria-menubutton";
import cx from "classnames";
import LpgCollapseOutline from "@/icons/Lpg/CollapseOutline";

import { useUniqueId } from "../hooks/useUniqueId";

import styles from "./Dropdown.scss";
import { ThemeContext } from "../Theme";
import { Icon } from "../Icon";

export const Dropdown = forwardRef((props, ref) => {
  const {
    label,
    items,
    featuredItems,
    onBlur: onItemBlur,
    onMenuToggle,
    onSelection: onItemSelection,
    customClass,
    listCustomClass,
    inputCustomClass,
    isValid,
    invalidMessage,
    hintMessage,
    disabled,
    emptyLabel,
    scheme,
    staticOpen,
    value: controlledValue,
    required,
    ...elementProps
  } = props;
  const { id } = elementProps;
  const dropdownId = id || `dropdown-${useUniqueId("Dropdown", props) + 1}`;

  const [lastControlledValue, setLastControlledValue] = useState(
    controlledValue
  );
  const [value, setValue] = useState(controlledValue);
  if (controlledValue !== lastControlledValue) {
    setLastControlledValue(controlledValue);
    setValue(controlledValue);
  }

  const [visitedOnce, setVisitedOnce] = useState(false);
  const [missingValueOnSubmit, setMissingValueOnSubmit] = useState(false);

  const context = useContext(ThemeContext);

  function onSelection(newValue, event) {
    setValue(newValue);
    setMissingValueOnSubmit(false);

    if (onItemSelection) {
      onItemSelection(newValue, event);
    }
  }

  function onChange(event) {
    setValue(event.currentTarget.value);
    setMissingValueOnSubmit(false);

    if (onItemSelection) {
      onItemSelection(event.currentTarget.value, event);
    }
  }

  function onBlur(event) {
    if (!visitedOnce) {
      setVisitedOnce(true);
    }
    if (onItemBlur) onItemBlur(value, event);
  }

  const handleInvalid = (e) => {
    e.preventDefault();

    if (e.target.validity.valueMissing) {
      setMissingValueOnSubmit(true);
      return;
    }

    setMissingValueOnSubmit(false);
  };

  const shouldDisplayInvalidState = () =>
    !isValid ||
    missingValueOnSubmit ||
    (required && visitedOnce && value === "");
  const MenuTag = staticOpen ? "div" : Menu;

  return (
    <div
      ref={ref}
      className={cx(scheme, customClass, {
        [styles[context]]: context,
        "is-disabled": disabled,
      })}
    >
      <Wrapper
        className={styles.wrapper}
        onSelection={onSelection}
        onMenuToggle={onMenuToggle}
        onBlur={onBlur}
      >
        <p
          className={cx(styles.input, inputCustomClass, {
            "is-active": value.length,
            "is-invalid": shouldDisplayInvalidState(),
          })}
        >
          {value || "\u00A0"}
        </p>

        <div className={styles.labelWrapper}>
          <Button
            className={styles.label}
            disabled={disabled}
            {...elementProps}
          >
            {label}
            {required ? "*" : null}
          </Button>
          <div className={styles.icon}>
            <Icon icon={LpgCollapseOutline} size={Icon.Size.xsmall} />
          </div>
        </div>

        <MenuTag className={styles.menu}>
          <ul className={cx(styles.list, listCustomClass)}>
            {featuredItems.map((item, index) => (
              <li
                key={item}
                className={cx(styles.item, {
                  [styles.lastFeaturedItem]: index === featuredItems.length - 1,
                })}
              >
                <MenuItem
                  className={cx(styles.itemButton, {
                    "is-selected": value === item,
                  })}
                >
                  {item}
                </MenuItem>
              </li>
            ))}
            {items.map((item) => (
              <li key={item} className={styles.item}>
                <MenuItem
                  className={cx(styles.itemButton, {
                    "is-selected": value === item,
                  })}
                >
                  {item}
                </MenuItem>
              </li>
            ))}
          </ul>
        </MenuTag>

        {hintMessage && <p className={styles.hint}>{hintMessage}</p>}

        {shouldDisplayInvalidState() && (
          <p className={styles.message}>{invalidMessage}</p>
        )}
      </Wrapper>

      <div className={styles.selectWrapper}>
        <>
          <select
            onInvalid={handleInvalid}
            id={dropdownId}
            name={dropdownId}
            className={cx(styles.select, inputCustomClass, {
              "is-active": value.length,
              "is-invalid": shouldDisplayInvalidState(),
            })}
            onChange={onChange}
            onBlur={onBlur}
            value={value}
            required={required}
            {...elementProps}
          >
            {emptyLabel && <option aria-label={emptyLabel} value="" />}
            {
              // eslint-disable-next-line jsx-a11y/control-has-associated-label
              emptyLabel === "" && <option hidden disabled value="" />
            }
            {featuredItems.map((item) => (
              <option key={item}>{item}</option>
            ))}
            {items.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>

          <div className={styles.labelWrapper}>
            <label htmlFor={dropdownId} className={styles.label}>
              {label}
              {required ? "*" : null}
            </label>
            <div className={styles.icon}>
              <Icon icon={LpgCollapseOutline} size={Icon.Size.xsmall} />
            </div>
          </div>

          {hintMessage && <p className={styles.hint}>{hintMessage}</p>}

          {shouldDisplayInvalidState() && (
            <p className={styles.message}>{invalidMessage}</p>
          )}
        </>
      </div>
    </div>
  );
});

Dropdown.propTypes = {
  label: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
  customClass: PropTypes.string,
  listCustomClass: PropTypes.string,
  inputCustomClass: PropTypes.string,
  featuredItems: PropTypes.arrayOf(PropTypes.string),
  staticOpen: PropTypes.bool,
  disabled: PropTypes.bool,
  isValid: PropTypes.bool,
  invalidMessage: PropTypes.string,
  hintMessage: PropTypes.string,
  onSelection: PropTypes.func,
  onMenuToggle: PropTypes.func,
  onBlur: PropTypes.func,
  required: PropTypes.bool,
  scheme: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
  ]),
  value: PropTypes.string,
  emptyLabel: PropTypes.string,
};

Dropdown.defaultProps = {
  scheme: styles.dropdown,
  featuredItems: [],
  staticOpen: false,
  customClass: undefined,
  listCustomClass: undefined,
  inputCustomClass: undefined,
  isValid: true,
  disabled: false,
  invalidMessage: "",
  hintMessage: "",
  onSelection: null,
  onMenuToggle: null,
  onBlur: null,
  required: false,
  value: "",
  emptyLabel: "",
};

Dropdown.Variant = {
  themeDark: "dropdown--theme_dark",
};

Dropdown.displayName = "Dropdown";

export default Dropdown;
